import React, { useState } from 'react';
import './DepositWithdrawalModal.css';

const DepositWithdrawalModal = ({ isOpen, onClose, account, onSave, loading, editingTransaction }) => {
  const [transactionType, setTransactionType] = useState(editingTransaction?.type || 'deposit'); // 'deposit' or 'withdrawal'
  const [amount, setAmount] = useState(editingTransaction?.amount?.toString() || '');
  const [description, setDescription] = useState(editingTransaction?.description || '');
  const [error, setError] = useState('');

  // Update form when editingTransaction changes
  React.useEffect(() => {
    if (editingTransaction) {
      setTransactionType(editingTransaction.type || 'deposit');
      setAmount(editingTransaction.amount?.toString() || '');
      setDescription(editingTransaction.description || '');
    } else if (isOpen && !editingTransaction) {
      // Reset form when opening for new transaction
      setTransactionType('deposit');
      setAmount('');
      setDescription('');
    }
  }, [editingTransaction, isOpen]);

  // Clean form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setAmount('');
      setDescription('');
      setTransactionType('deposit');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    try {
      // Determine valid source enum value based on account type
      // Note: Backend only accepts: 'manual', 'cod', 'order' as valid enum values
      let validSource = 'manual'; // Default to 'manual' for manual transactions
      
      if (account?.paymentMethod === 'cod' || account?.id === 'cod' || account?._id === 'cod') {
        validSource = 'cod';
      } else if (account?.paymentMethod === 'order' || account?.source === 'order') {
        validSource = 'order';
      } else {
        // For all other accounts (bank, wallet, investment, etc.), use 'manual'
        // Investment accounts also use 'manual' as source since backend doesn't accept 'investment'
        validSource = 'manual';
      }
      
      // Ensure we have the correct account ID and name
      const accountId = account?._id || account?.id;
      const accountName = account?.name || 'Manual Transaction';
      const paymentMethod = account?.paymentMethod || validSource;
      
      // For COD accounts, we might not have a real account ID, so handle it differently
      const isCOD = account?.paymentMethod === 'cod' || account?.id === 'cod' || account?._id === 'cod';
      
      // When editing, preserve paymentAccount from existing transaction if available
      const existingPaymentAccount = editingTransaction?.paymentAccount?._id || editingTransaction?.paymentAccount || editingTransaction?.accountId;
      
      // Determine paymentAccount ID:
      // 1. If editing and transaction has paymentAccount, use it
      // 2. Otherwise, use account ID if valid (not COD virtual account)
      const paymentAccountId = editingTransaction && existingPaymentAccount 
        ? existingPaymentAccount 
        : (accountId && accountId !== 'cod' && !isCOD ? accountId : null);
      
      // Build transaction data according to backend API specification
      const transactionData = {
        type: transactionType,
        amount: parseFloat(amount),
        currency: 'PKR', // Default currency
        source: validSource, // Use valid enum value ('manual', 'cod', 'order')
        description: description.trim(),
        status: 'completed',
        // Backend expects paymentAccount field (MongoDB ObjectId) for linking to payment account
        // Only send paymentAccount if we have a valid account ID (not for COD virtual accounts)
        ...(paymentAccountId && { 
          paymentAccount: paymentAccountId // ✅ Payment Account ID (MongoDB ObjectId)
        }),
        // Also send paymentMethod if available (backend can auto-set from paymentAccount)
        ...(paymentMethod && paymentMethod !== 'cod' && { 
          paymentMethod: paymentMethod 
        }),
        // Keep accountName for reference (not required by backend but useful)
        accountName: accountName
      };
      
      // Debug: Log transaction data being sent
      console.log('Creating transaction for account:', {
        paymentAccount: transactionData.paymentAccount,
        accountId: accountId,
        accountName: transactionData.accountName,
        paymentMethod: transactionData.paymentMethod,
        source: transactionData.source,
        accountObject: account,
        type: transactionData.type,
        amount: transactionData.amount,
        isCOD: isCOD
      });

      await onSave(transactionData);
      
      // Reset form
      setAmount('');
      setDescription('');
      setTransactionType('deposit');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save transaction');
    }
  };

  const handleClose = () => {
    setAmount('');
    setDescription('');
    setTransactionType('deposit');
    setError('');
    onClose();
  };

  return (
    <div className="deposit-withdrawal-modal-overlay" onClick={handleClose}>
      <div className="deposit-withdrawal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className={`fas fa-${transactionType === 'deposit' ? 'arrow-down' : 'arrow-up'}`}></i>
            {editingTransaction ? 'Edit Transaction' : (transactionType === 'deposit' ? 'Deposit' : 'Withdrawal')}
          </h2>
          <button className="modal-close-btn" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          <div className="account-info">
            <div className="account-icon">
              <i className={`fas fa-${account?.icon || 'wallet'}`}></i>
            </div>
            <div className="account-details">
              <div className="account-name">{account?.name || account?.source || 'Account'}</div>
              <div className="account-balance">
                Balance: {account?.balance !== undefined ? `PKR ${account.balance.toLocaleString()}` : 'N/A'}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="transaction-form">
            <div className="form-group">
              <label>Transaction Type</label>
              <div className="type-selector">
                <button
                  type="button"
                  className={`type-btn ${transactionType === 'deposit' ? 'active deposit' : ''}`}
                  onClick={() => setTransactionType('deposit')}
                >
                  <i className="fas fa-arrow-down"></i>
                  Deposit
                </button>
                <button
                  type="button"
                  className={`type-btn ${transactionType === 'withdrawal' ? 'active withdrawal' : ''}`}
                  onClick={() => setTransactionType('withdrawal')}
                >
                  <i className="fas fa-arrow-up"></i>
                  Withdrawal
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="amount">
                Amount <span className="required">*</span>
              </label>
              <div className="amount-input-wrapper">
                <span className="currency-symbol"></span>
                <input
                  type="number"
                  id="amount"
                  step="0.01"
                  min="0.01"
                  value={amount && amount !== '' ? amount : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Remove any non-numeric characters except decimal point
                    const cleanValue = value.replace(/[^0-9.]/g, '');
                    setAmount(cleanValue);
                  }}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">
                Description <span className="required">*</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter transaction description..."
                rows="3"
                required
              />
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="btn-cancel" onClick={handleClose}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    {editingTransaction ? 'Update Transaction' : (transactionType === 'deposit' ? 'Deposit' : 'Withdraw')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DepositWithdrawalModal;

